import type { ButtonProps } from '@gitbook/spine-react';

/**
 * Base definition of an action returned by useUpgrade* hooks.
 * It's a set of ButtonProps with a discriminator and a less strict onPress event handler.
 */
export interface UseUpgradeAction extends ButtonProps {
    /** discriminator for type narrowing */
    _type: 'upgrade' | 'site-upgrade' | 'contact-support' | 'self-serve-migrate' | 'contact-sales';

    /** Optional label to display on upgrade CTA badges, defaults to the button label */
    badgeLabel?: string;

    /** upgrade actions onPress doesn't need to require an event */
    onPress: () => Promise<void>;
}

/**
 * Action to to prompt the user to upgrade to a core plan.
 */
export interface UpgradeAction extends UseUpgradeAction {
    _type: 'upgrade' | 'contact-sales';

    /** The required plan for the given feature key */
    plan?: string;
}

/**
 * Action to prompt the user to upgrade to a site plan.
 */
export interface SiteUpgradeAction extends UseUpgradeAction {
    _type: 'site-upgrade';

    /** The required plan for the given feature key */
    plan: string;
}

/**
 * Action to to prompt the user to contact support.
 */
export interface ContactSupportAction extends UseUpgradeAction {
    _type: 'contact-support';
}

/**
 * Action to to prompt the user to self-serve migrate.
 */
export interface SelfServeMigrateAction extends UseUpgradeAction {
    _type: 'self-serve-migrate';
}

/**
 * Action type guards
 */
export function isUpgradeAction(action?: UseUpgradeAction): action is UpgradeAction {
    return action?._type === 'upgrade';
}
export function isSiteUpgradeAction(action: UseUpgradeAction | null): action is SiteUpgradeAction {
    return action?._type === 'site-upgrade';
}
export function isContactSupportAction(action?: UseUpgradeAction): action is ContactSupportAction {
    return action?._type === 'contact-support';
}

export function isSelfServeMigrateAction(
    action: UseUpgradeAction | null
): action is SelfServeMigrateAction {
    return action?._type === 'self-serve-migrate';
}
